import { html, nothing } from "lit";
import { icons } from "../icons.ts";
import type { ChatAttachment } from "../ui-types.ts";
import {
  attachmentIconColorClassForAttachment,
  attachmentIconForRenderKind,
} from "./attachment-icon-color.ts";
import {
  mapComposerAttachmentToOverlay,
  openAttachmentPreviewOverlay,
  type AttachmentPreviewMode,
} from "./attachment-preview-overlay.ts";
import {
  attachmentIsPreviewable,
  attachmentKindForMime,
  attachmentPersistedStatusLabel,
  resolveAttachmentPersistedStatus,
  type AttachmentKind,
  type AttachmentPersistedStatus,
} from "./attachment-types.ts";
import { classifyFile } from "./chat-attachment-classifier.ts";
import type { ChatProps, StoredAttachmentResult } from "./chat-view-props.ts";
import {
  composerInlinePreviewKind,
  composerInlinePreviewSrc,
  composerPreviewSnippet,
  type ComposerInlinePreviewKind,
} from "./composer-attachment-inline-preview.ts";
import {
  SessionFileError,
  describeSessionFile,
  formatAttachmentBytes,
  mediaTypeLabel,
  shortFilename,
} from "./session-files.ts";

/**
 * Composer attachment pipeline.
 *
 * Every accepted file is uploaded to the session-file store at attach time
 * and referenced by id from then on. That single change is what fixes the
 * three failures from the recording:
 *
 *   - a PNG that reached the model by a side channel but never appeared in
 *     the transcript — the bytes are now a first-class stored file, so the
 *     transcript has something durable to render;
 *   - a "near-black thumbnail" chip — the chip now uses the gateway's real
 *     preview derivative, not a blob URL the browser may never decode;
 *   - a 2.1 MiB mp4 rejected as `unsupported type: video/mp4` — there is no
 *     type allowlist left. A failed file is marked on its own chip.
 */

/**
 * Original `File` for every attachment currently in the composer, keyed by
 * the placeholder id. Powers the retry button so a failed upload can be
 * retried without the operator re-picking the file.
 */
const pendingFilesByAttachmentId = new Map<string, File>();

function generateAttachmentId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Only revoke blob URLs; server preview URLs have no lifetime we own. */
function releasePreviewUrl(attachment: ChatAttachment): void {
  if (attachment.previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(attachment.previewUrl);
  }
}

function describeUploadFailure(err: unknown): string {
  if (err instanceof SessionFileError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * The composer's attachment list as it is *right now*.
 *
 * Never read `props.attachments` inside an async pipeline. Lit re-renders on
 * every `onAttachmentsChange` and builds a fresh props object, so the `props`
 * captured by a handler that has already awaited a store call still points at
 * the pre-await array. Merging against it maps over chips that are not there
 * yet and commits an empty list — which is how a stored file ended up with no
 * chip, no preview, and no id on the wire.
 */
function readAttachments(props: ChatProps): ChatAttachment[] {
  return props.getAttachments?.() ?? props.attachments ?? [];
}

/**
 * Classify a batch of files and feed them into the composer's attachment
 * list. Each file is stored immediately, so the chip flips from "storing…"
 * to a real thumbnail the moment the gateway responds — and Send stays
 * disabled until every chip has finished.
 */
async function processIncomingFiles(files: File[], props: ChatProps): Promise<void> {
  if (!props.onAttachmentsChange) return;
  let current = [...readAttachments(props)];
  for (const file of files) {
    const outcome = classifyFile(file, current.length);
    if (!outcome.ok) {
      // Rejections live on a chip too — the operator sees exactly which
      // file was refused and why, and the rest of the batch still lands.
      const placeholder: ChatAttachment = {
        id: generateAttachmentId(),
        mimeType: "",
        name: file.name,
        size: file.size,
        uploading: false,
        status: "unsupported",
        error: outcome.rejected.reason,
      };
      current = [...current, placeholder];
      props.onAttachmentsChange(current);
      continue;
    }

    const placeholder: ChatAttachment = {
      id: generateAttachmentId(),
      mimeType: outcome.file.effectiveMime,
      name: file.name,
      size: file.size,
      uploading: true,
    };
    current = [...current, placeholder];
    props.onAttachmentsChange(current);
    pendingFilesByAttachmentId.set(placeholder.id, file);

    let stored: StoredAttachmentResult | null;
    try {
      stored = (await props.onAttachmentUpload?.(file)) ?? null;
    } catch (err) {
      // Re-sync `current` from the committed list: the failure path updates the
      // app state, and a stale local would resurrect this chip as "storing…"
      // (and drop its error) on the next file's commit.
      current = markFailed(current, placeholder.id, describeUploadFailure(err), props);
      continue;
    }
    if (!stored) {
      current = markFailed(current, placeholder.id, "could not store this file", props);
      continue;
    }

    current = readAttachments(props).map((a) =>
      a.id === placeholder.id
        ? {
            ...a,
            sessionFileId: stored.sessionFileId,
            attachmentId: stored.attachmentId,
            mimeType: stored.mediaType,
            name: stored.filename,
            size: stored.size,
            kind: stored.kind,
            hasPreview: Boolean(stored.previewUrl),
            previewUrl: stored.previewUrl,
            uploading: false,
            status: "ready",
            error: undefined,
          }
        : a,
    );
    props.onAttachmentsChange(current);
    pendingFilesByAttachmentId.delete(placeholder.id);

    // JAISIU-3533 — pull the prepared preview for a spreadsheet or a
    // document so the chip in the composer shows the *content*, not just a
    // type glyph.
    //
    // Before this, the composer chip had no preview at all: the upload
    // result carries only `previewUrl` (the image derivative), so
    // `composerPreviewSnippet()` had nothing to render and the operator
    // saw a bare icon next to the filename. The gateway already prepares
    // the tabular schema and the extracted text — the chip just never
    // asked for it.
    void loadComposerPreview(placeholder.id, stored, props);
  }
}

/**
 * JAISIU-3533 — fetch the prepared preview for one stored attachment and
 * merge it into the chip.
 *
 * Only the document family is worth a round trip here: a spreadsheet
 * becomes a table, a document becomes its extracted text. Images and
 * media already have their inline preview, and a `code` / `file` chip has
 * no body to show.
 *
 * The fetch is best-effort. A gateway with nothing prepared simply leaves
 * the chip with its icon — the preview never invents content.
 */
async function loadComposerPreview(
  attachmentId: string,
  stored: {
    sessionFileId: string;
    filename: string;
    mediaType: string;
    size: number;
    kind?: string;
  },
  props: ChatProps,
): Promise<void> {
  const loader = props.loadSessionFilePreview;
  if (!loader) return;

  const kind = composerRenderKind({
    kind: stored.kind,
    mimeType: stored.mediaType,
  });
  if (kind !== "spreadsheet" && kind !== "document" && kind !== "pdf") return;

  let loaded: ChatAttachment | null = null;
  try {
    loaded = await loader({
      fileId: stored.sessionFileId,
      filename: stored.filename,
      mediaType: stored.mediaType,
      size: stored.size,
    });
  } catch {
    // A failed preview fetch is not an error the operator needs to see:
    // the chip keeps its icon and its filename.
    return;
  }
  if (!loaded) return;

  const tabular = loaded.tabular;
  const text = (loaded.text ?? "").trim();
  if (!tabular && !text) return;

  props.onAttachmentsChange?.(
    readAttachments(props).map((a) =>
      a.id === attachmentId
        ? {
            ...a,
            ...(tabular ? { tabular } : {}),
            ...(text ? { text } : {}),
          }
        : a,
    ),
  );
}

/**
 * Mark one chip as failed and return the committed list.
 *
 * Returns the list so the caller can keep its local copy in step with app
 * state; `current` is otherwise a stale mirror after the first failure.
 */
function markFailed(
  current: ChatAttachment[],
  id: string,
  reason: string,
  props: ChatProps,
): ChatAttachment[] {
  const next = readAttachments(props).map((a) =>
    a.id === id ? { ...a, uploading: false, status: "error" as const, error: reason } : a,
  );
  props.onAttachmentsChange?.(next);
  return next;
}

/**
 * Re-run the store call for a failed attachment. The original `File` is
 * cached by id, so retry does not require re-picking from disk.
 */
async function retryAttachmentUpload(att: ChatAttachment, props: ChatProps): Promise<void> {
  const file = pendingFilesByAttachmentId.get(att.id);
  if (!file || !props.onAttachmentsChange) return;

  const uploading = readAttachments(props).map((a) =>
    a.id === att.id ? { ...a, uploading: true, error: undefined } : a,
  );
  props.onAttachmentsChange(uploading);

  let stored: StoredAttachmentResult | null;
  try {
    stored = (await props.onAttachmentUpload?.(file)) ?? null;
  } catch (err) {
    stored = null;
    markFailed(uploading, att.id, describeUploadFailure(err), props);
    return;
  }
  if (!stored) {
    markFailed(uploading, att.id, "could not store this file", props);
    return;
  }

  const previous = readAttachments(props).find((a) => a.id === att.id);
  if (previous) releasePreviewUrl(previous);
  props.onAttachmentsChange(
    readAttachments(props).map((a) =>
      a.id === att.id
        ? {
            ...a,
            sessionFileId: stored.sessionFileId,
            attachmentId: stored.attachmentId,
            mimeType: stored.mediaType,
            name: stored.filename,
            size: stored.size,
            kind: stored.kind,
            hasPreview: Boolean(stored.previewUrl),
            previewUrl: stored.previewUrl,
            uploading: false,
            error: undefined,
          }
        : a,
    ),
  );
  pendingFilesByAttachmentId.delete(att.id);
}

/** True when every chip has finished storing — gates the Send button. */
export function attachmentsReady(attachments: ChatAttachment[] | undefined): boolean {
  return (attachments ?? []).every((a) => !a.uploading);
}

export function handleAttachInputChange(e: Event, props: ChatProps) {
  const input = e.target as HTMLInputElement;
  const files = input.files;
  if (!files?.length) {
    input.value = "";
    return;
  }
  // run() the async pipeline so this synchronous event handler returns
  // immediately and the UI can keep painting.
  void processIncomingFiles(Array.from(files), props);
  input.value = "";
}

export function scrollFleetQueue() {
  document.querySelector(".fleet-v2-panel--send #chat-fleet-queue")?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
  });
}

/**
 * Read the current DOM selection as plain text, or `null` if there is no
 * selection (or we are not in a browser context — the Selection API is
 * `undefined` under node).
 *
 * Exported for tests so we can prove that bubble selection captures only the
 * substring the user actually highlighted (UX-R1, JAISIU-3297). The render
 * code must never reach into this value to mutate the composer — selection
 * stays a pure read-only observation.
 */
export function readSelectionText(): string | null {
  const sel = (globalThis as { getSelection?: () => Selection | null }).getSelection?.();
  if (!sel) return null;
  const text = sel.toString();
  return text.length > 0 ? text : null;
}

/**
 * Composer paste handler.
 *
 * Invariant (UX-R1 / JAISIU-3297): the composer textarea must never receive
 * the full bubble text on a selection or copy. We achieve this two ways:
 *
 *   1. We only intercept paste events that carry file items. When the user
 *      copies a substring of a bubble and pastes into the composer, no file
 *      items are present and we return without calling `preventDefault()`,
 *      so the browser's default text-insert path puts ONLY the selected
 *      substring (whatever is on the clipboard) into the textarea.
 *   2. We deliberately do NOT read `event.target.value` here. Reading the
 *      textarea value after a copy event is exactly the regression the
 *      ticket guards against — it lets a full bubble leak in if some
 *      upstream handler has filled the textarea.
 *
 * Exported so node tests can drive the function directly.
 */
export function handlePaste(e: ClipboardEvent, props: ChatProps) {
  const items = e.clipboardData?.items;
  if (!items) return;

  const files: File[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (file) files.push(file);
  }

  // No files → let the browser handle the text paste. By NOT calling
  // preventDefault(), we guarantee the textarea only receives whatever is
  // on the clipboard (the selected substring from the bubble, not the
  // whole bubble text).
  if (files.length === 0) return;

  // Always preventDefault when files are present so the browser doesn't
  // insert anything into the textarea — even if the target check below
  // bails (e.g. synthetic paste events in node tests with no target).
  e.preventDefault();

  // JAISIU-3326 — only honor a paste that originated inside the composer.
  // The textarea's `@paste` handler is naturally scoped, but if anything
  // ever bubbles up from a chat-bubble selection (e.g. a future global
  // listener), we still want the textarea's own Selection API range to
  // drive the insert point — never a range from outside the textarea.
  const target = e.target as HTMLTextAreaElement | null;
  if (!target || typeof target.selectionStart !== "number") {
    return;
  }

  void processIncomingFiles(files, props);
}

export function handleDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
}

export function handleDragEnter(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  const target = e.currentTarget as HTMLElement;
  target.classList.add("composer--drag-over");
}

export function handleDragLeave(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  const target = e.currentTarget as HTMLElement;
  // Only remove class if we're leaving the composer entirely
  const relatedTarget = e.relatedTarget as HTMLElement | null;
  if (!relatedTarget || !target.contains(relatedTarget)) {
    target.classList.remove("composer--drag-over");
  }
}

export function handleDrop(e: DragEvent, props: ChatProps) {
  e.preventDefault();
  e.stopPropagation();
  const target = e.currentTarget as HTMLElement;
  target.classList.remove("composer--drag-over");

  const files = e.dataTransfer?.files;
  if (!files?.length) return;
  void processIncomingFiles(Array.from(files), props);
}

/** A chip is "stored" once it has an id and finished uploading. */
export function isAttachmentStored(att: ChatAttachment): boolean {
  return !att.uploading && Boolean(att.sessionFileId ?? att.attachmentId) && !att.error;
}

/**
 * Render the composer's attachment tray.
 *
 * Each chip shows a real thumbnail when the gateway produced one, a type
 * icon plus filename and size otherwise, and a remove button. Failures are
 * marked on the chip itself with the reason — never as a banner, because
 * the rest of the message is fine.
 */
export function renderAttachmentPreview(props: ChatProps) {
  const attachments = props.attachments ?? [];
  if (attachments.length === 0) {
    return nothing;
  }

  const removeAttachment = (att: ChatAttachment) => {
    releasePreviewUrl(att);
    pendingFilesByAttachmentId.delete(att.id);
    props.onAttachmentsChange?.(readAttachments(props).filter((a) => a.id !== att.id));
  };

  return html`
    <div class="chat-attachments">
      ${attachments.map((att) => {
        if (att.error) {
          return html`
            <div
              class="chat-attachment chat-attachment--error"
              role="alert"
              title=${att.error}
              data-testid="chat-attachment-error"
              data-attachment-id=${att.id}
              data-attachment-name=${att.name ?? ""}
            >
              <span
                class="chat-attachment__icon ${attachmentIconColorClassForAttachment({
                  kind: att.kind,
                  mimeType: att.mimeType,
                  mime: att.mime,
                  filename: att.filename ?? att.name,
                })}"
                data-attachment-icon-variant=${attachmentIconColorClassForAttachment({
                  kind: att.kind,
                  mimeType: att.mimeType,
                  mime: att.mime,
                  filename: att.filename ?? att.name,
                })}
                aria-hidden="true"
              >
                ${attachmentIconForRenderKind(composerRenderKind(att), {
                  mimeType: att.mimeType,
                  mime: att.mime,
                  filename: att.filename ?? att.name,
                })}
              </span>
              <span class="chat-attachment__meta">
                <span class="chat-attachment__name">${shortFilename(att.name ?? "file")}</span>
                <span class="chat-attachment__sub">${att.error}</span>
              </span>
              <button
                class="chat-attachment__retry"
                type="button"
                title="Retry storing this file"
                aria-label="Retry attachment upload"
                data-testid="chat-attachment-retry"
                @click=${(e: MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void retryAttachmentUpload(att, props);
                }}
              >
                Retry
              </button>
              <button
                class="chat-attachment__remove"
                type="button"
                title="Remove this file from the outgoing message."
                aria-label="Remove attachment"
                @click=${() => removeAttachment(att)}
              >
                ${icons.x}
              </button>
            </div>
          `;
        }

        if (att.uploading) {
          return html`
            <div
              class="chat-attachment chat-attachment--uploading"
              data-testid="chat-attachment-uploading"
              data-attachment-id=${att.id}
            >
              <span class="chat-attachment__spinner" aria-hidden="true"></span>
              <span class="chat-attachment__meta">
                <span class="chat-attachment__name">${shortFilename(att.name ?? "file")}</span>
                <span class="chat-attachment__sub"
                  >${formatAttachmentBytes(att.size ?? 0)} · storing…</span
                >
              </span>
              <button
                class="chat-attachment__remove"
                type="button"
                title="Remove this file from the outgoing message."
                aria-label="Remove attachment"
                @click=${() => removeAttachment(att)}
              >
                ${icons.x}
              </button>
            </div>
          `;
        }

        const thumbSrc = att.previewUrl;
        // JAISIU-4427 — the draft tray opens the *same* unified overlay
        // as the transcript, for every kind. Before this, only the
        // `thumbSrc` branch (i.e. only files the gateway had already
        // produced a preview derivative for — in practice images) had a
        // click handler, so a draft video / audio / PDF chip rendered
        // an inert type icon the operator could not preview.
        // JAISIU-4435 — resolve the render kind through the shared
        // helper so the chip's `data-attachment-kind`, its previewability
        // gate, and its icon all agree. Previously this used the MIME
        // alone while the icon used the gateway kind, so a chip could
        // declare one kind and draw another.
        const renderKind = composerRenderKind(att);
        const previewable = attachmentIsPreviewable({ kind: renderKind });
        // JAISIU-4451 — the nav batch holds the *previewable* draft
        // attachments only, exactly like the transcript chips. A
        // `code` / `file` member has no overlay body, so including it
        // made ← / → land on the download-fallback panel.
        const previewableAttachments = attachments.filter((a) =>
          attachmentIsPreviewable({ kind: composerRenderKind(a) }),
        );
        const batch: ChatAttachment[] = previewableAttachments.map((a) =>
          mapComposerAttachmentToOverlay(a, {
            resolveSessionFileUrl: resolveSessionFileUrlFromProps(props),
          }),
        );
        const batchIndex = previewableAttachments.findIndex((a) => a.id === att.id);
        const openPreview = () =>
          openComposerAttachmentPreview(
            att,
            thumbSrc ?? "",
            resolveSessionFileUrlFromProps(props),
            batch,
            batchIndex,
            loadSessionFilePreviewFromProps(props),
          );
        // JAISIU-4452 — the inline preview decides *what the chip shows*
        // next to the filename: a still thumbnail for an image, a
        // playable frame for a video, a playable strip for an audio
        // note, and a document glyph (with a prepared-text snippet
        // when the gateway has one) for a document-family file.
        const inline = composerInlinePreviewKind(att, renderKind);
        const snippet = composerPreviewSnippet(att, renderKind);
        const persisted = resolveAttachmentPersistedStatus(att);
        return html`
          <div
            class="chat-attachment chat-attachment--status-${persisted}"
            data-testid="chat-attachment"
            data-attachment-status=${persisted}
            data-attachment-id=${att.id}
            data-attachment-kind=${renderKind}
            data-attachment-preview=${inline}
            data-session-file-id=${att.sessionFileId ?? ""}
            data-attachment-name=${att.name ?? ""}
            title=${describeSessionFile({
              filename: att.name ?? "file",
              size: att.size ?? 0,
              mediaType: att.mimeType,
            })}
          >
            ${renderComposerInlinePreview({
              att,
              inline,
              renderKind,
              previewable,
              openPreview,
              resolveSessionFileUrl: resolveSessionFileUrlFromProps(props),
            })}
            <span class="chat-attachment__meta">
              <span class="chat-attachment__name">${shortFilename(att.name ?? "file")}</span>
              <span class="chat-attachment__sub"> ${chipSizeLine(att)} </span>
              ${renderAttachmentStatusBadge(persisted)}
              ${
                snippet
                  ? html`<span
                      class="chat-attachment__snippet"
                      data-testid="chat-attachment-snippet"
                      title=${snippet}
                      >${snippet}</span
                    >`
                  : nothing
              }
            </span>
            ${renderComposerDownloadButton(att, resolveSessionFileUrlFromProps(props))}
            <button
              class="chat-attachment__remove"
              type="button"
              title="Remove this file from the outgoing message."
              aria-label="Remove attachment"
              @click=${() => removeAttachment(att)}
            >
              ${icons.x}
            </button>
          </div>
        `;
      })}
    </div>
  `;
}

/** `2.1 MB · MP4` — the chip's second line. Status lives in the badge. */
function chipSizeLine(att: ChatAttachment): string {
  return [formatAttachmentBytes(att.size ?? 0), mediaTypeLabel(att.mimeType)]
    .filter(Boolean)
    .join(" · ");
}

function renderAttachmentStatusBadge(status: AttachmentPersistedStatus) {
  const icon =
    status === "processing"
      ? icons.loader
      : status === "ready"
        ? icons.check
        : status === "unsupported"
          ? icons.alertTriangle
          : icons.x;
  return html`
    <span
      class="chat-attachment__status chat-attachment__status--${status}"
      data-testid="chat-attachment-status"
      title=${attachmentPersistedStatusLabel(status)}
      >${icon} ${attachmentPersistedStatusLabel(status)}</span
    >
  `;
}

/**
 * JAISIU-4452 — the chip's inline preview element.
 *
 * The image branch keeps the *exact* look it had before this ticket:
 * a 36×36 `<img>` thumbnail in the chip's leading slot. The other
 * branches are new:
 *
 *   - **video** — a `<video>` frame (muted, `playsinline`, no
 *     autoplay) so the operator sees the first frame instead of a bare
 *     glyph. Clicking it opens the shared overlay with the real player.
 *   - **audio** — a compact `<audio controls>` strip so a voice note is
 *     playable in place. The controls are their own click target, so
 *     the strip does **not** open the overlay (the operator asked to
 *     play it, not to page through the batch).
 *   - **document family** (pdf / spreadsheet / document) — the coloured
 *     document glyph. The prepared preview text, when the gateway has
 *     it, is rendered as the chip's snippet line.
 *   - **code / file** — the plain type glyph; there is no preview body
 *     for those kinds.
 */
function renderComposerInlinePreview(opts: {
  att: ChatAttachment;
  inline: ComposerInlinePreviewKind;
  renderKind: AttachmentKind;
  previewable: boolean;
  openPreview: () => void;
  resolveSessionFileUrl?: (fileId: string) => string | undefined;
}): unknown {
  const { att, inline, renderKind, previewable, openPreview } = opts;
  const rawUrl = att.sessionFileId ? opts.resolveSessionFileUrl?.(att.sessionFileId) : undefined;
  const { src, fallbackTo } = composerInlinePreviewSrc({ ...att, url: rawUrl ?? att.url }, rawUrl);
  const iconClass = `chat-attachment__icon chat-attachment__icon--clickable ${attachmentIconColorClassForAttachment(
    {
      kind: att.kind,
      mimeType: att.mimeType,
      mime: att.mime,
      filename: att.filename ?? att.name,
    },
  )}`;

  if (inline === "image") {
    if (!src) {
      return renderComposerTypeGlyph(att, renderKind, iconClass, previewable, openPreview);
    }
    return html`<img
      src=${src}
      alt=${`Preview of ${att.name ?? "attached file"}`}
      class="chat-attachment__img"
      data-testid="chat-attachment-img"
      title="Click to preview"
      @click=${openPreview}
      @error=${(ev: Event) => {
        // A preview derivative can 404 even when the record claimed one
        // (derivative pruned, `sharp` unavailable). Fall back to the
        // full-resolution raw URL rather than leaving a broken image.
        const img = ev.currentTarget as HTMLImageElement | null;
        if (!img || !fallbackTo || img.dataset.fallbackApplied === "1") return;
        img.dataset.fallbackApplied = "1";
        img.src = fallbackTo;
      }}
    />`;
  }

  if (inline === "video") {
    if (!src) {
      return renderComposerTypeGlyph(att, renderKind, iconClass, previewable, openPreview);
    }
    return html`<span
      class="chat-attachment__media chat-attachment__media--video"
      data-testid="chat-attachment-video-wrap"
    >
      <video
        class="chat-attachment__video"
        src=${src}
        preload="metadata"
        playsinline
        muted
        data-testid="chat-attachment-video"
        title=${att.name ?? "attached video"}
        @click=${(ev: Event) => {
          // A click on the *controls* must not bubble into the overlay.
          const target = ev.target as HTMLElement | null;
          if (target?.closest("button")) return;
          openPreview();
        }}
        @error=${(ev: Event) => {
          const v = ev.currentTarget as HTMLVideoElement | null;
          if (!v || !fallbackTo || v.dataset.fallbackApplied === "1") return;
          v.dataset.fallbackApplied = "1";
          v.src = fallbackTo;
        }}
      ></video>
    </span>`;
  }

  if (inline === "audio") {
    if (!src) {
      return renderComposerTypeGlyph(att, renderKind, iconClass, previewable, openPreview);
    }
    return html`<span
      class="chat-attachment__media chat-attachment__media--audio"
      data-testid="chat-attachment-audio-wrap"
    >
      <audio
        class="chat-attachment__audio"
        src=${src}
        controls
        preload="metadata"
        data-testid="chat-attachment-audio"
        title=${att.name ?? "attached audio"}
        @error=${(ev: Event) => {
          const a = ev.currentTarget as HTMLAudioElement | null;
          if (!a || !fallbackTo || a.dataset.fallbackApplied === "1") return;
          a.dataset.fallbackApplied = "1";
          a.src = fallbackTo;
        }}
      ></audio>
    </span>`;
  }

  return renderComposerTypeGlyph(att, renderKind, iconClass, previewable, openPreview);
}

/**
 * The chip's type glyph — a button that opens the shared overlay.
 *
 * Used for the document family (pdf / spreadsheet / document) and for
 * the kinds that have no preview body at all (code / file). For the
 * latter the button is disabled: there is nothing to open, and a
 * disabled control is honest about it.
 */
function renderComposerTypeGlyph(
  att: ChatAttachment,
  renderKind: AttachmentKind,
  iconClass: string,
  previewable: boolean,
  openPreview: () => void,
): unknown {
  return html`<button
    type="button"
    class=${iconClass}
    data-testid="chat-attachment-icon"
    data-attachment-icon-variant=${attachmentIconColorClassForAttachment({
      kind: att.kind,
      mimeType: att.mimeType,
      mime: att.mime,
      filename: att.filename ?? att.name,
    })}
    aria-label=${`Preview ${att.name ?? "attached file"}`}
    title="Click to preview"
    ?disabled=${!previewable}
    @click=${openPreview}
  >
    ${attachmentIconForRenderKind(renderKind, {
      mimeType: att.mimeType,
      mime: att.mime,
      filename: att.filename ?? att.name,
    })}
  </button>`;
}

/**
 * JAISIU-4452 — the chip's **separate** download action.
 *
 * The chip's leading slot is the *preview* affordance (thumbnail /
 * frame / glyph); downloading is its own button, so the two never
 * compete for the same click. The button is hidden while the file is
 * still uploading (there is nothing to download yet) and for chips
 * that failed (there is no stored file to download).
 *
 * The URL precedence mirrors the overlay's: the full-resolution raw
 * URL when the gateway knows the session file, otherwise the local
 * blob / data URL the chip already carries.
 */
function renderComposerDownloadButton(
  att: ChatAttachment,
  resolveSessionFileUrl?: (fileId: string) => string | undefined,
): unknown {
  const rawUrl = att.sessionFileId ? resolveSessionFileUrl?.(att.sessionFileId) : undefined;
  const url = rawUrl ?? att.dataUrl ?? att.url ?? "";
  if (!url) return nothing;
  const filename = att.filename ?? att.name ?? "attachment";
  return html`<a
    class="chat-attachment__download"
    href=${url}
    download=${filename}
    rel="noopener noreferrer"
    title=${`Download ${filename}`}
    aria-label=${`Download ${filename}`}
    data-testid="chat-attachment-download"
    @click=${(e: MouseEvent) => {
      // Stop the click from reaching the chip's preview handler.
      e.stopPropagation();
    }}
  >
    ${icons.download}
  </a>`;
}

/**
 * Open the unified preview overlay for a composer attachment.
 *
 * The composer's `ChatAttachment` shape is wider than the
 * message-side one (it carries `previewUrl`, `uploading`, etc.); we
 * map it to the narrow `ChatAttachment` shape the unified overlay
 * expects via {@link mapComposerAttachmentToOverlay} so the body
 * is loaded from the *full-resolution* source (dataUrl >
 * sessionFileId raw > previewUrl). The overlay is mounted in
 * `pre-send` mode — the download button is hidden because the
 * file lives on the local disk and the operator does not need a
 * "download my own upload" affordance.
 *
 * `resolveSessionFileUrl` is supplied by the host (via
 * `ChatProps.resolveSessionFileUrl`) so an already-uploaded chip —
 * one that no longer carries the original `dataUrl` because the
 * upload has committed — still resolves the session file to the
 * gateway's raw URL instead of falling through to the thumbnail.
 * Without this adapter, JAISIU-4147 would still scale a 256×256
 * preview up to the full overlay size for any composer chip that
 * has been on the wire long enough to be persisted.
 *
 * The `thumbSrc` parameter is kept for back-compat with the chip
 * click handler; the helper now derives the body URL from `att`
 * itself (so the chip no longer pins the overlay to the
 * thumbnail).
 *
 * Exported for unit tests so the mapping can be asserted without
 * a DOM.
 */
export function openComposerAttachmentPreview(
  att: ChatAttachment,
  thumbSrc: string,
  resolveSessionFileUrl?: (fileId: string) => string | undefined,
  batch?: ChatAttachment[],
  batchIndex?: number,
  loadPreview?: (a: ChatAttachment) => Promise<ChatAttachment | null>,
): void {
  // thumbSrc is kept in the signature so the existing call site
  // does not change; mapComposerAttachmentToOverlay derives the
  // body URL from `att` directly (dataUrl wins over
  // previewUrl/thumbnail).
  void thumbSrc;
  const mode: AttachmentPreviewMode = "pre-send";
  const mapped = mapComposerAttachmentToOverlay(att, { resolveSessionFileUrl });
  // JAISIU-4427 — pass the whole draft tray as the nav batch so a
  // multi-file message can be walked with ← / → without closing.
  openAttachmentPreviewOverlay(mapped, {
    mode,
    ...(batch && batch.length > 1 ? { batch } : {}),
    ...(typeof batchIndex === "number" && batchIndex >= 0 ? { batchIndex } : {}),
    // JAISIU-4449 — let the overlay pull the prepared preview
    // (tabular schema / extracted text) for a spreadsheet or document.
    ...(loadPreview ? { loadPreview } : {}),
  });
}

/**
 * Adapter: `ChatProps.resolveSessionFileUrl` takes a `{fileId, filename, mediaType, size}`
 * record (the message-side shape) but the composer's helper only has a
 * bare `fileId`. Wrap it so the call site stays one line and `undefined`
 * is passed through cleanly when the host does not supply a resolver.
 */
function resolveSessionFileUrlFromProps(props: ChatProps): (fileId: string) => string | undefined {
  const host = props.resolveSessionFileUrl;
  if (!host) {
    return (): string | undefined => undefined;
  }
  return (fileId) => {
    const url = host({ fileId, filename: "", mediaType: "", size: 0 });
    return url || undefined;
  };
}

/**
 * JAISIU-4449 — adapter that turns the host's `loadSessionFilePreview`
 * into the overlay's `loadPreview` shape.
 *
 * The overlay knows the attachment (and therefore the session file id);
 * the host knows the credentials and the HTTP origin. This adapter
 * bridges the two without either side learning the other's concerns.
 */
function loadSessionFilePreviewFromProps(
  props: ChatProps,
): ((a: ChatAttachment) => Promise<ChatAttachment | null>) | undefined {
  const host = props.loadSessionFilePreview;
  if (!host) return undefined;
  return async (a: ChatAttachment) => {
    const fileId = a.sessionFileId ?? a.id;
    if (!fileId) return null;
    const loaded = await host({
      fileId,
      filename: a.filename ?? a.name ?? "",
      mediaType: a.mime ?? a.mimeType ?? "",
      size: a.sizeBytes ?? a.size ?? 0,
    });
    if (!loaded) return null;
    // Carry the overlay's own identity forward: the host resolves the
    // preview payload, but the overlay still owns the attachment's id,
    // url and kind.
    return {
      ...a,
      ...loaded,
      id: a.id,
      sessionFileId: a.sessionFileId ?? loaded.sessionFileId,
      url: a.url ?? loaded.url,
      kind: a.kind ?? loaded.kind,
    };
  };
}

/**
 * JAISIU-4435 — the render kind for a composer chip.
 *
 * A stored chip carries the **gateway** kind (`SessionFileKind`), where a
 * PDF is `document` and an Excel file is `tabular`. That vocabulary is too
 * coarse to pick an icon (a PDF would show the generic document glyph) and
 * too coarse to pick a preview body (a PDF would render as extracted text
 * instead of a PDF viewer).
 *
 * The MIME is the finer signal, so it wins whenever it is present and
 * recognised. Only when the MIME is missing or unrecognised do we fall back
 * to the gateway kind (mapped through the same render vocabulary).
 */
function composerRenderKind(att: {
  kind?: string;
  mimeType?: string;
  mime?: string;
}): AttachmentKind {
  const mime = (att.mimeType ?? att.mime ?? "").trim();
  if (mime) {
    const fromMime = attachmentKindForMime(mime);
    // `attachmentKindForMime` returns "file" for anything it does not
    // recognise; in that case the gateway's kind is the better signal.
    if (fromMime !== "file") return fromMime;
  }
  return attachmentKindForMime(kindToMime(att.kind));
}

/** Map a gateway kind onto a MIME so the render-kind mapper can read it. */
function kindToMime(kind: string | undefined): string {
  switch (kind) {
    case "image":
      return "image/*";
    case "video":
      return "video/*";
    case "audio":
      return "audio/*";
    case "tabular":
      return "text/csv";
    case "text":
      return "text/plain";
    case "document":
      return "application/msword";
    default:
      return "";
  }
}

/** Best-effort kind for a chip that stored before the server told us. */
